'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as React from 'react'

import { cn } from '~/lib/utils'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

const TooltipRoot = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
    useTouch?: boolean
    useTap?: boolean
  }
>(({ useTouch = false, useTap = false, children, ...props }, _ref) => {
  const [open, setOpen] = React.useState(false)

  const handleTouch = (event: React.TouchEvent | React.MouseEvent) => {
    event.persist()
    setOpen(true)
  }

  return (
    <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && useTouch) {
          return React.cloneElement(child as React.ReactElement, {
            onTouchStart: handleTouch,
            onMouseDown: handleTouch,
          })
        }
        return child
      })}
    </TooltipPrimitive.Root>
  )
})
TooltipRoot.displayName = TooltipPrimitive.Root.displayName

export { Tooltip, TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger }
